import { useFormikContext } from "formik";
import { useEffect, useRef } from "react";
import usePrevious from "./usePrevious";

export type Selector = string;
export type Target = Element | Selector;
export type ScrollPosition = { readonly x: number; readonly y: number };

const querySelector = (
  selectors: Selector,
  parent: ParentNode = document
): Element | undefined => {
  try {
    const result = parent.querySelector(selectors);
    return result === null ? undefined : result;
  } catch {
    console.warn(`Syntax error in selector [${selectors}].`);
    return undefined;
  }
};

export const elementFromTarget = (
  target: Target,
  parent: ParentNode = document
): Element | undefined => {
  return target instanceof Element ? target : querySelector(target, parent);
};
export const getScrollPosition = (): ScrollPosition => {
  // See https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollX#Notes
  const documentElement =
    // documentElement can in fact be undefined in some old browsers.

    document.documentElement || document.body.parentNode || document.body;
  // scrollX can in fact be undefined in some old browsers.

  const x = window.scrollX || window.pageXOffset || documentElement.scrollLeft;
  // scrollY can in fact be undefined in some old browsers.

  const y = window.scrollY || window.pageYOffset || documentElement.scrollTop;

  return { x, y };
};
export const prefersReducedMotion = (): boolean => {
  // See https://caniuse.com/#feat=matchmedia
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
};
export const setScrollPosition = (
  scrollPosition: ScrollPosition,
  smoothScroll = false
): void => {
  if (!smoothScroll || prefersReducedMotion()) {
    // Use old form of scrollTo() (when we can) to maximize browser compatibility.
    window.scrollTo(scrollPosition.x, scrollPosition.y);
  } else {
    try {
      window.scrollTo({
        behavior: "smooth",
        left: scrollPosition.x,
        top: scrollPosition.y,
      });
    } catch {
      // If scrollTo with options throws, fall back on old form.
      // See https://github.com/Fyrd/caniuse/issues/1760
      // See https://github.com/frontarm/navi/issues/71
      // See https://github.com/frontarm/navi/pull/84/files
      window.scrollTo(scrollPosition.x, scrollPosition.y);
    }
  }
};

const getScrollPositionRestorer = (): (() => void) => {
  const scrollPosition = getScrollPosition();

  return () => {
    setScrollPosition(scrollPosition);
  };
};

const withRestoreScrollPosition = <T>(funcWithScrollSideEffect: () => T): T => {
  const restoreScrollPosition = getScrollPositionRestorer();
  const result = funcWithScrollSideEffect();
  restoreScrollPosition();
  return result;
};

export const focusElement = async (
  target: Target,
  preventScroll = false
): Promise<boolean> => {
  // See: https://developer.paciellogroup.com/blog/2014/08/using-the-tabindex-attribute/
  // See: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus#Browser_compatibility
  // See: https://github.com/whatwg/html/issues/834
  // See: https://stackoverflow.com/questions/4963053/focus-to-input-without-scrolling/6610501

  const element = elementFromTarget(target);

  if (element === undefined) {
    console.warn(
      // TODO fix this
      `Cannot focus element. Element [${target.toString()}] not found.`
    );
    return Promise.resolve(false);
  }

  if (!(element instanceof HTMLElement || element instanceof SVGElement)) {
    console.warn(
      // TODO fix this
      `Cannot focus element. Element [${target.toString()}] is not an HTMLElement or SVGElement.`
    );
    return Promise.resolve(false);
  }

  try {
    // Set tabindex="-1" if necessary.
    // TODO avoid setting tabindex when we're confident we don't need to?
    if (!element.hasAttribute("tabindex")) {
      element.setAttribute("tabindex", "-1");
      // We remove tabindex after blur to avoid weird browser behavior
      // where a mouse click can activate elements with tabindex="-1".
      const blurListener = (): void => {
        element.removeAttribute("tabindex");
        element.removeEventListener("blur", blurListener);
      };
      element.addEventListener("blur", blurListener);
    }

    if (preventScroll) {
      // preventScroll has poor browser support, so we restore scroll manually after setting focus.
      // see https://caniuse.com/#feat=mdn-api_htmlelement_focus_preventscroll_option
      // TODO detect if browser supports preventScroll and avoid `withRestoreScrollPosition`
      // shenanigans if so.
      withRestoreScrollPosition(() => {
        try {
          element.focus({ preventScroll: true });
        } catch {
          // If focus() with options throws, fall back on calling focus() without any arguments.
          element.focus();
        }
      });
    } else {
      // Avoid passing anything to focus() (when we can) to maximize browser compatibility.
      element.focus();
    }

    return document.activeElement === element;
  } catch (e: unknown) {
    // Apparently trying to focus a disabled element in IE can throw.
    // See https://stackoverflow.com/a/1600194/2476884
    console.error(e);
    return false;
  }
};
export const scrollIntoView = (
  element: Element,
  smoothScroll = false
): void => {
  const headerOffset = 330;
  // TODO support ScrollIntoViewOptions and respect block and inline even when not smooth scrolling.

  // Scrolling to the document element or the body is problematic
  // for a few reasons so we just scroll to `0, 0` instead.
  // See e.g. https://github.com/iamdustan/smoothscroll/issues/138
  if (element === document.documentElement || element === document.body) {
    setScrollPosition({ x: 0, y: 0 }, smoothScroll);
  } else {
    var elementPosition = element.getBoundingClientRect().top;
    var offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    if (!smoothScroll || prefersReducedMotion()) {
      // Avoid passing anything to scrollIntoView() (when we can) to maximize browser compatibility.
      //   element.scrollIntoView();
      window.scrollTo({
        top: offsetPosition,
      });
    } else {
      try {
        // element.scrollIntoView({ behavior: "smooth" });
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      } catch {
        // If scrollIntoView with options throws, fall back on no options.
        // See https://github.com/frontarm/navi/issues/71
        // element.scrollIntoView();
        window.scrollTo({
          top: offsetPosition,
        });
      }
    }
  }
};
export const isInViewport = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();

  // TODO: handle overflow-scroll in element's container?
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      // innerHeight can in fact be undefined in some old browsers.
      (window.innerHeight || document.documentElement.clientHeight) &&
    // innerHeight can in fact be undefined in some old browsers.
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};
export const scrollIntoViewIfRequired = (
  target: Target,
  smoothScroll = false,
  inViewport: typeof isInViewport = isInViewport
): void => {
  const element = elementFromTarget(target);
  if (element !== undefined && !inViewport(element)) {
    scrollIntoView(element, smoothScroll);
  }
};

const focusAndScrollIntoViewIfRequired = async (
  focusTarget: Target,
  scrollIntoViewTarget: Target,
  smoothScroll = false
): Promise<boolean> => {
  const elementToFocus = elementFromTarget(focusTarget);
  const elementToScrollIntoView =
    elementFromTarget(scrollIntoViewTarget) ?? elementToFocus;

  // See https://css-tricks.com/smooth-scrolling-accessibility/
  // See https://github.com/whatwg/html/issues/834
  // See https://stackoverflow.com/questions/4963053/focus-to-input-without-scrolling/6610501

  // If we're not smooth scrolling and
  // elementToFocus === elementToScrollIntoView then we can
  // avoid preventScroll shenanigans.
  const preventScroll =
    smoothScroll || elementToFocus !== elementToScrollIntoView;

  // Focus the element for keyboard users and users of assistive technology.
  const result =
    elementToFocus !== undefined &&
    (await focusElement(elementToFocus, preventScroll));

  if (elementToScrollIntoView !== undefined) {
    // For screen users, scroll the element into view.
    scrollIntoViewIfRequired(elementToScrollIntoView, smoothScroll);
  }

  return result;
};

export const useFocusInvalidField = <
  T extends HTMLElement,
  U extends HTMLElement
>(
  name: string,
  smoothScroll?: boolean
) => {
  const formikContext = useFormikContext();
  // The input/select itself, this is what we will be focusing.
  const fieldRef = useRef<T | null>(null);
  // The container element that contains both the input/select and its label.
  // This is what we will be scrolling into view. Doing this helps guarantee that
  // the label will be visible. If the submit button is at the bottom of the form and the
  // label is above the invalid input, _just_ focusing the input could leave the
  // label offscreen (above the viewport) which can make it difficult for the user to
  // identify the invalid field.
  const containerElementRef = useRef<U | null>(null);

  const wasSubmitting = usePrevious(formikContext.isSubmitting);

  useEffect(() => {
    const firstError = Object.keys(formikContext.errors)[0];
    if (
      (wasSubmitting === true || formikContext.isSubmitting) &&
      firstError === name &&
      fieldRef.current !== null
    ) {
      // Use a helper function from https://github.com/oaf-project/oaf-side-effects.
      // This does some browser compatibility and accessibility work for us.

      focusAndScrollIntoViewIfRequired(
        fieldRef.current, // focus the input/select
        containerElementRef.current ?? fieldRef.current, // scroll the container (and label) into view if set, otherwise scroll to the input
        smoothScroll // optionally use smooth scroll. Accessibility note: this will ignore smooth scroll if the user has indicated a preference for reduced motion.
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wasSubmitting,
    formikContext.isSubmitting,
    name,
    // formikContext.errors,
    smoothScroll,
  ]);
  return { fieldRef, containerElementRef };
};
